<script setup lang="ts">
import {
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions, ITableState } from '@modules/tables/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VRouterTabs from '@components/tabs/VRouterTabs/VRouterTabs.vue';
import CreativePreview from '@modules/affiliate-program-creatives/components/CreativePreview/CreativePreview.vue';
import { useAffiliateProgramCreativePreview } from '@modules/affiliate-program-creatives/composables';
import {
  AFFILIATE_TRACKERS_LIST_ROUTE_NAME,
} from '@modules/affiliate-trackers/constants';
import AffiliateCreativesCopyButton
  from '@modules/affiliate-trackers/submodules/creatives/components/AffiliateCreativesCopyButton/AffiliateCreativesCopyButton.vue';
import {
  AFFILIATE_TRACKERS_CREATIVES_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATE_CREATIVES,
} from '@modules/affiliate-trackers/submodules/creatives/constants';
import {
  useAffiliateTrackerCreativesListQuery,
  useAffiliateTrackersCreativeOptionsQuery,
} from '@modules/affiliate-trackers/submodules/creatives/queries';
import { FORMAT_MONTH_YEAR_DAY } from '@modules/core/composables/datetime/constants';
import { useDownloadImageByUrl } from '@modules/core/composables/download-image-by-url';
import { useI18n } from '@modules/i18n/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import {
  isOptionalString,
  isString,
} from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

const router = useRouter();
const route = useRoute();
const { downloadImage } = useDownloadImageByUrl();

const routeType = computed(() => (
  isOptionalString(route.params.type) && route.params.type !== 'list' ? route.params.type : ''
));
const id = computed(() => (
  isString(route.params.id) ? Number(route.params.id) : 0
));

const { getTranslation } = useI18n();
const {
  data: options,
  isLoading: isLoadingOptions,
} = useAffiliateTrackersCreativeOptionsQuery();

const creativeTablesState = reactive<Record<string, ITableState>>({
  link: { page: 1 },
  jpeg: { page: 1 },
  png: { page: 1 },
  gif: { page: 1 },
  bannerflow_gif: { page: 1 },
  iframe: { page: 1 },
});

const allCreativeOptions = Object.keys(creativeTablesState);

const availableCreativeOptions = computed(() => (
  allCreativeOptions.filter((item) => !!options.value?.active_type[item])
));

const tableTabs = computed<VRouterTab[]>(() => (
  availableCreativeOptions.value.map((item) => ({
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, `TAB_${item.toUpperCase()}`).value,
    route: {
      name: AFFILIATE_TRACKERS_CREATIVES_ROUTE_NAME,
      params: {
        type: item,
        id: id.value,
      },
    },
  }))
));

const isNotAvailable = ref(false);

watchImmediate(availableCreativeOptions, (newValue) => {
  if (isLoadingOptions.value || route.name !== AFFILIATE_TRACKERS_CREATIVES_ROUTE_NAME) {
    return;
  }

  if (newValue) {
    const availableTab = newValue.find((item) => item === routeType.value);

    if (!availableTab) {
      if (newValue.length > 0) {
        void router.replace({
          name: AFFILIATE_TRACKERS_CREATIVES_ROUTE_NAME,
          params: {
            type: newValue[0],
            id: id.value,
          },
        });
      } else {
        isNotAvailable.value = true;
      }
    }
  }
});

const isDisabled = ref(false);

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliateTrackerCreativesListQuery, routeType, isDisabled);

function onTabClick(): void {
  isDisabled.value = true;
}

watchImmediate(tableState, (newState) => {
  if (routeType.value) {
    creativeTablesState[routeType.value] = newState;
  }
});

watch(routeType, (newValue) => {
  if (newValue) {
    updateTableState(creativeTablesState[newValue]);
    isDisabled.value = false;
  }
});

const tableFilterOptions = computed<ITableFilterProps>(() => ({
  pathForFilter: `api2/affiliate/creatives/${routeType.value}`,
  actionForFilter: 'list',
  filterName: `creative_${routeType.value}`,
}));

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  title: {
    type: ESmartFilterColumnType.CHECKBOX,
    defaultMobilePreview: true,
  },
  advertising_company: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  language: {
    type: ESmartFilterColumnType.CHECKBOX,
    localization: {
      translationPath: 'common.languages.',
    },
  },
  at_beginning_show: {
    type: ESmartFilterColumnType.DATE,
    dateFormat: FORMAT_MONTH_YEAR_DAY,
  },
  at_end_show: {
    type: ESmartFilterColumnType.DATE,
    dateFormat: FORMAT_MONTH_YEAR_DAY,
  },
  created_at: {
    type: ESmartFilterColumnType.DATE,
  },
  resolution: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};

const {
  previewCreative,
  onPreview,
  onPreviewHide,
} = useAffiliateProgramCreativePreview();
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateTrackersCreativesListPage'"
    :type="isNotAvailable ? 'form' : 'table'"
    :title="isNotAvailable ? $getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'HEADER') : undefined"
  >
    <div
      v-if="isNotAvailable"
    >
      <div
        class="mb-6 text-muted-color"
      >
        {{ $getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'NO_CREATIVES') }}
      </div>
      <VBackButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'BACK')"
        :to="{
          name: AFFILIATE_TRACKERS_LIST_ROUTE_NAME,
        }"
      />
    </div>
    <VTable
      v-else
      :data="data"
      :state="creativeTablesState[routeType]"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'HEADER')"
      :filter-props="tableFilterOptions"
      :columns-options="columnOptions"
      :empty-label-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'NO_CREATIVES')"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #tabs>
        <VRouterTabs
          :items="tableTabs"
          replace
          @click="onTabClick"
        />
      </template>
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="listItem.format !== 'link'"
          icon="pi-eye"
          @click="onPreview(listItem)"
        />
        <VIconButton
          v-if="listItem.format === 'jpeg' || listItem.format === 'png' || listItem.format === 'gif'"
          icon="pi-download"
          @click="downloadImage(listItem.img, listItem.banner_file_name || listItem.title)"
        />
        <AffiliateCreativesCopyButton
          :tracker-id="id"
          :creative-id="listItem.id"
          :title="listItem.title"
        />
      </template>
      <template
        #footerButtons
      >
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'BACK')"
          :to="{
            name: AFFILIATE_TRACKERS_LIST_ROUTE_NAME,
          }"
        />
      </template>
    </VTable>
    <CreativePreview
      :creative="previewCreative"
      @hide="onPreviewHide"
    />
  </VCard>
</template>
